import React from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../lib/usePageMeta";

/**
 * "This URL is no longer part of the storefront" page.
 *
 * Used for the retired COA public routes (`/coa-library`, `/coa/*`), the
 * never-supported `/reviews` paths, and any other class of URL we
 * deliberately withdraw from the public catalog.  Renders a `noindex,
 * nofollow, noarchive` robots meta so any crawler that lands here drops
 * the page from its index on next crawl.  Also sets a low-priority
 * canonical to the home page so a stray inbound link is at least
 * de-duplicated.
 */
const COPY = {
  "coa-library": {
    title: "Batch documents are shared on request",
    body:  "A Certificate of Analysis (COA) is a lab document that lists a batch’s test results. We don’t publish COAs on the public site — email us the batch number from your packing list and we’ll share what’s available.",
  },
  coa: {
    title: "This document is not publicly listed",
    body:  "A Certificate of Analysis (COA) is a lab document listing a specific batch’s test results. Email the batch number from your packing list and we’ll share what’s available.",
  },
  reviews: {
    title: "Reviews are not published on this site",
    body:  "We don’t host customer reviews on the storefront. Every research peptide is batch-tracked and ships with a signature at delivery.",
  },
  default: {
    title: "This page is no longer available",
    body:  "The URL you followed is no longer part of the storefront.",
  },
};

export default function ObsoleteGone({ kind = "default" }) {
  const copy = COPY[kind] || COPY.default;
  usePageMeta({
    title: copy.title,
    description: copy.body,
    canonicalPath: "/",
    // The important bit: strong deindex signal for the crawler.
    robots: "noindex, nofollow, noarchive",
  });
  return (
    <div className="bg-brand-surface min-h-[60vh]" data-testid={`gone-${kind}`}>
      <div className="mps-container py-24 md:py-32 max-w-2xl">
        <div className="eyebrow mb-4">No longer available</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-brand-navy leading-tight">
          {copy.title}
        </h1>
        <p className="mt-4 text-brand-muted text-lg leading-relaxed">
          {copy.body}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/quality"
            data-testid={`gone-cta-${kind === "reviews" ? "shop" : "quality"}`}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white text-sm font-semibold"
          >
            {kind === "reviews" ? "Browse the catalog" : "See how documents work"}
          </Link>
          <Link
            to="/contact"
            data-testid="gone-cta-contact"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-[12px] border border-brand-border text-brand-navy hover:bg-white text-sm font-semibold"
          >
            Contact us
          </Link>
        </div>
      </div>
    </div>
  );
}
